import { defineComponent as _defineComponent } from 'vue';
import { createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString } from "vue";
const _hoisted_1 = { class: "slider-container" };
const _hoisted_2 = {
    key: 0,
    class: "background-ranges"
};
const _hoisted_3 = ["title"];
const _hoisted_4 = {
    key: 1,
    class: "colored-tracks"
};
const _hoisted_5 = ["min", "max", "step"];
const _hoisted_6 = { class: "slider-labels" };
const _hoisted_7 = { class: "important-time-labels" };
const _hoisted_8 = { class: "time-text" };
const _hoisted_9 = {
    key: 0,
    class: "current-value mt-2"
};
const _hoisted_10 = { class: "flex items-center justify-center" };
import { ref, watch, computed } from 'vue';
import { useTheme } from '../../composables/useTheme';
export default /*@__PURE__*/ _defineComponent({
    __name: 'AdminRangeSlider',
    props: {
        value: {},
        absoluteMin: {},
        absoluteMax: {},
        showValue: { type: Boolean, default: true },
        step: { default: 1 },
        color: { default: 'blue' },
        formatValue: {},
        backgroundRanges: {},
        backgroundStyle: { default: 'dashed' },
        operatingHours: {}
    },
    emits: ["update:value"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { theme } = useTheme();
        // Local state
        const localValue = ref(props.value);
        // ✅ GRÜNE BEREICHE ERKENNEN
        // ✅ ZEITLABELS: Nur für wichtige Zeiten (Öffnungs-/Schließzeiten)
        const importantTimes = computed(() => {
            const times = [];
            if (props.operatingHours?.length) {
                for (const hours of props.operatingHours) {
                    const openMinutes = timeToMinutes(hours.open);
                    const closeMinutes = timeToMinutes(hours.close);
                    // Nur anzeigen wenn im Slider-Bereich
                    if (openMinutes >= props.absoluteMin && openMinutes <= props.absoluteMax) {
                        times.push({
                            minutes: openMinutes,
                            label: hours.open,
                            type: 'open'
                        });
                    }
                    if (closeMinutes >= props.absoluteMin && closeMinutes <= props.absoluteMax) {
                        times.push({
                            minutes: closeMinutes,
                            label: hours.close,
                            type: 'close'
                        });
                    }
                }
            }
            return times.sort((a, b) => a.minutes - b.minutes);
        });
        // Helper function
        const timeToMinutes = (time) => {
            if (!time || !time.includes(':'))
                return 0;
            const [hours, minutes] = time.split(':').map(Number);
            return hours * 60 + minutes;
        };
        const hasGreenRanges = computed(() => {
            return props.backgroundRanges?.some(range => range.color === 'green' || range.label?.includes('Verfügbar')) || false;
        });
        const greenRanges = computed(() => {
            const ranges = props.backgroundRanges?.filter(range => range.color === 'green' || range.label?.includes('Verfügbar')) || [];
            // ✅ Debug: Zeige gefundene grüne Bereiche
            console.log('🎯 AdminRangeSlider: Gefundene grüne Bereiche:', ranges.map(r => ({
                start: r.start,
                end: r.end,
                label: r.label,
                color: r.color
            })));
            return ranges;
        });
        const withOpacity = (hexColor, opacity) => {
            const hex = (hexColor || '').replace('#', '');
            if (!/^[0-9a-fA-F]{6}$/.test(hex))
                return hexColor;
            const alpha = Math.round(Math.min(1, Math.max(0, opacity)) * 255).toString(16).padStart(2, '0');
            return `#${hex}${alpha}`;
        };
        const sliderThemeVars = computed(() => ({
            '--slider-primary': theme.value.colors.primary || '#2563eb',
            '--slider-warning': theme.value.colors.warning || theme.value.colors.primary || '#d97706',
            '--slider-warning-hover': theme.value.colors.warningHover || theme.value.colors.primaryHover || '#b45309',
            '--slider-warning-35': withOpacity(theme.value.colors.warning || theme.value.colors.primary || '#d97706', 0.35),
            '--slider-warning-50': withOpacity(theme.value.colors.warning || theme.value.colors.primary || '#d97706', 0.5),
            '--slider-primary-20': withOpacity(theme.value.colors.primary || '#2563eb', 0.2)
        }));
        // ✅ SLIDER CONSTRAINTS: Nur in grünen Bereichen
        const constrainedMin = computed(() => {
            if (!hasGreenRanges.value || !greenRanges.value.length)
                return props.absoluteMin;
            return Math.min(...greenRanges.value.map(r => r.start));
        });
        const constrainedMax = computed(() => {
            if (!hasGreenRanges.value || !greenRanges.value.length)
                return props.absoluteMax;
            return Math.max(...greenRanges.value.map(r => r.end));
        });
        // Watch for prop changes
        watch(() => props.value, (newValue) => {
            localValue.value = newValue;
        });
        // Methods
        const getBackgroundClass = (label) => {
            if (!label)
                return 'background-dashed'; // Default
            // Erkennung basierend auf Label-Text
            if (label.includes('Vergangen'))
                return 'background-gray';
            if (label.includes('Vorbereitung'))
                return 'background-orange';
            if (label.includes('Verfügbar'))
                return 'background-green';
            if (label.includes('Abgelaufene Öffnungszeit'))
                return 'background-gray'; // Abgelaufene Zeiten = grau
            if (label.includes('Öffnungszeiten'))
                return 'background-light-gray'; // Aktuelle Öffnungszeiten = hellgrau
            return 'background-green'; // Default zu verfügbar
        };
        const handleChange = () => {
            // ✅ CONSTRAINT: Nur Werte in grünen Bereichen erlauben
            if (!hasGreenRanges.value) {
                console.log(`🎯 AdminRangeSlider: Keine grünen Bereiche verfügbar`);
                return;
            }
            console.log(`🎯 AdminRangeSlider: Slider-Änderung zu ${localValue.value}, greenRanges.length = ${greenRanges.value.length}`);
            // ✅ 5-MINUTEN-SPRÜNGE: Snape zu 5-Minuten-Intervallen, außer bei Min/Max
            let snappedValue = localValue.value;
            // Prüfe ob es Min oder Max eines grünen Bereichs ist
            const isMinOrMax = greenRanges.value.some((range) => localValue.value === range.start || localValue.value === range.end);
            if (!isMinOrMax) {
                // Snape zu 5-Minuten-Intervallen (z.B. 17:00, 17:05, 17:10, etc.)
                const remainder = localValue.value % 5;
                if (remainder !== 0) {
                    // Runde zur nächsten 5-Minuten-Marke
                    snappedValue = remainder < 2.5
                        ? localValue.value - remainder
                        : localValue.value + (5 - remainder);
                }
            }
            // Prüfe ob der (evtl. gesnappte) Wert in einem grünen Bereich liegt
            const isInGreenRange = greenRanges.value.some((range) => snappedValue >= range.start && snappedValue <= range.end);
            console.log(`🎯 AdminRangeSlider: snappedValue = ${snappedValue}, isInGreenRange = ${isInGreenRange}`);
            if (isInGreenRange) {
                localValue.value = snappedValue;
                emit('update:value', snappedValue);
                console.log(`🎯 AdminRangeSlider: ✅ Wert akzeptiert: ${snappedValue}`);
            }
            else {
                // Snape zum nächsten grünen Bereich
                const nearestGreen = findNearestGreenValue(snappedValue);
                localValue.value = nearestGreen;
                emit('update:value', nearestGreen);
                console.log(`🎯 AdminRangeSlider: ❌ Wert korrigiert: ${snappedValue} → ${nearestGreen}`);
            }
        };
        // ✅ TRACK FARB-KLASSEN basierend auf color-Eigenschaft (bessere Methode)
        const getTrackColorClass = (range) => {
            if (!range) {
                console.log('🎨 AdminRangeSlider: Kein range → track-gray');
                return 'track-gray';
            }
            // Direkte Farb-Eigenschaft hat Vorrang
            if (range.color) {
                const cssClass = `track-${range.color}`;
                console.log(`🎨 AdminRangeSlider: range.color="${range.color}" → ${cssClass}`, range);
                return cssClass;
            }
            // Fallback auf Label-basierte Erkennung
            const label = range.label?.toLowerCase() || '';
            let cssClass = 'track-gray';
            if (label.includes('verfügbar'))
                cssClass = 'track-green';
            else if (label.includes('vorbereitung'))
                cssClass = 'track-orange';
            console.log(`🎨 AdminRangeSlider: label="${range.label}" → ${cssClass}`, range);
            return cssClass;
        };
        // Hilfsfunktion: Finde nächsten Wert in grünem Bereich
        const findNearestGreenValue = (targetValue) => {
            if (!greenRanges.value.length)
                return constrainedMin.value;
            const maxClamp = constrainedMax.value;
            let nearest = constrainedMin.value;
            let minDistance = Infinity;
            for (const range of greenRanges.value) {
                // Distance to start of range
                const distanceToStart = Math.abs(targetValue - range.start);
                if (distanceToStart < minDistance) {
                    minDistance = distanceToStart;
                    nearest = range.start;
                }
                // Distance to end of range
                const distanceToEnd = Math.abs(targetValue - range.end);
                if (distanceToEnd < minDistance) {
                    minDistance = distanceToEnd;
                    nearest = range.end;
                }
                // If within range, use target value
                if (targetValue >= range.start && targetValue <= range.end) {
                    return targetValue;
                }
            }
            return Math.min(nearest, maxClamp);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: "single-range-slider",
                style: _normalizeStyle(sliderThemeVars.value)
            }, [
                _createElementVNode("div", _hoisted_1, [
                    _createCommentVNode(" Background Ranges (Öffnungszeiten) "),
                    (_ctx.backgroundRanges?.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.backgroundRanges, (range, index) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: index,
                                    class: _normalizeClass(["background-range", getBackgroundClass(range.label)]),
                                    style: _normalizeStyle({
                                        left: `${((range.start - _ctx.absoluteMin) / (_ctx.absoluteMax - _ctx.absoluteMin)) * 100}%`,
                                        width: `${((range.end - range.start) / (_ctx.absoluteMax - _ctx.absoluteMin)) * 100}%`,
                                        zIndex: 10 - index
                                    }),
                                    title: range.label
                                }, null, 14 /* CLASS, STYLE, PROPS */, _hoisted_3));
                            }), 128 /* KEYED_FRAGMENT */))
                        ]))
                        : _createCommentVNode("v-if", true),
                    _createCommentVNode(" Track "),
                    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "slider-track" }, null, -1 /* CACHED */)),
                    _createCommentVNode(" ✅ FARBIGE TRACK SEGMENTE "),
                    (_ctx.backgroundRanges?.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.backgroundRanges, (range, index) => {
                                return (_openBlock(), _createElementBlock("div", {
                                    key: `track-${index}`,
                                    class: _normalizeClass(["track-segment", getTrackColorClass(range)]),
                                    style: _normalizeStyle({
                                        left: `${((range.start - _ctx.absoluteMin) / (_ctx.absoluteMax - _ctx.absoluteMin)) * 100}%`,
                                        width: `${((range.end - range.start) / (_ctx.absoluteMax - _ctx.absoluteMin)) * 100}%`
                                    })
                                }, null, 6 /* CLASS, STYLE */));
                            }), 128 /* KEYED_FRAGMENT */))
                        ]))
                        : _createCommentVNode("v-if", true),
                    _createCommentVNode(" Single Handle "),
                    _withDirectives(_createElementVNode("input", {
                        type: "range",
                        min: _ctx.absoluteMin,
                        max: _ctx.absoluteMax,
                        step: _ctx.step,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((localValue).value = $event)),
                        class: _normalizeClass(["slider-input", `slider-input-${_ctx.color}`]),
                        onInput: handleChange
                    }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_5), [
                        [
                            _vModelText,
                            localValue.value,
                            void 0,
                            { number: true }
                        ]
                    ])
                ]),
                _createCommentVNode(" Value Labels "),
                _createElementVNode("div", _hoisted_6, [
                    _createCommentVNode(" ✅ WICHTIGE ZEITLABELS: Nur Öffnungs-/Schließzeiten "),
                    _createElementVNode("div", _hoisted_7, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(importantTimes.value, (time, index) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: `time-${index}`,
                                class: _normalizeClass(["time-label", time.type === 'open' ? 'time-label-open' : 'time-label-close']),
                                style: _normalizeStyle({
                                    left: `${((time.minutes - _ctx.absoluteMin) / (_ctx.absoluteMax - _ctx.absoluteMin)) * 100}%`
                                })
                            }, [
                                _createElementVNode("span", _hoisted_8, _toDisplayString(time.label), 1 /* TEXT */)
                            ], 6 /* CLASS, STYLE */));
                        }), 128 /* KEYED_FRAGMENT */))
                    ])
                ]),
                _createCommentVNode(" Current Value Display "),
                (_ctx.showValue)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createElementVNode("div", _hoisted_10, [
                            _createElementVNode("span", {
                                class: _normalizeClass(`text-${_ctx.color}-600 font-medium`)
                            }, _toDisplayString(_ctx.formatValue ? _ctx.formatValue(localValue.value) : localValue.value), 3 /* TEXT, CLASS */)
                        ])
                    ]))
                    : _createCommentVNode("v-if", true)
            ], 4 /* STYLE */));
        };
    }
});
